'use client';
import classNames from 'classnames';
import { useState } from 'react';
import AnimationImage from '../animation-image/animation-image';
import AnimationFadeLazy from '../animation-fade-lazy/animation-fade-lazy';
import { LazyMotion, m as motion } from 'framer-motion';
import { variantsFadeInUp } from '../__constants__/animation-variants';
import { CldImage } from 'next-cloudinary';
const loadFeatures = () => import('../__constants__/framer-features').then((res) => res.default);

export interface CardSquareProps {
    overline: string;
    author?: string;
    date?: string | null;
    year?: number | null;
    title: string;
    summary: string;
    imageUrl: string;
    imageAltText?: string;
    mode?: 'dark' | 'light';
    addXPadding?: boolean;
}

export function CardSquare(props: CardSquareProps) {
    const {
        overline,
        author,
        date,
        year,
        title,
        summary,
        imageUrl,
        imageAltText = '',
        mode = 'light',
        addXPadding = false,
    } = props;
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const isDark = mode === 'dark';
    const textClasses = classNames({
        'text-white': mode === 'dark',
        'text-gray-900': mode === 'light',
    });
    const bgClasses = classNames({
        'bg-white': mode === 'light',
        'bg-gray-900': mode === 'dark',
    });

    return (
        <div className={`h-[450px] relative mb-12 ${addXPadding ? 'mx-2' : ''}`}>
            <div>
                <div className={`${bgClasses} absolute -bottom-12 -right-px z-10 w-[80%] px-8 py-8 xl:px-14`}>
                    <AnimationFadeLazy className="space-y-3 ">
                        <>
                            <LazyMotion features={loadFeatures}>
                                <motion.h4 variants={variantsFadeInUp} className={`${textClasses} h5 overline`}>
                                    {overline}
                                    {year ? ` • ${year}` : ''}
                                </motion.h4>
                            </LazyMotion>
                            {author && (
                                <LazyMotion features={loadFeatures}>
                                    <motion.div variants={variantsFadeInUp} className={`${textClasses} text-xs`}>
                                        By {author}
                                    </motion.div>
                                </LazyMotion>
                            )}
                            {date && (
                                <LazyMotion features={loadFeatures}>
                                    <motion.div variants={variantsFadeInUp} className={`${textClasses} text-xs`}>
                                        {date}
                                    </motion.div>
                                </LazyMotion>
                            )}
                            <LazyMotion features={loadFeatures}>
                                <motion.h5
                                    variants={variantsFadeInUp}
                                    className={`h-md ${textClasses} group-hover:text-green-400`}
                                >
                                    {title}
                                </motion.h5>
                            </LazyMotion>
                            <LazyMotion features={loadFeatures}>
                                <motion.p variants={variantsFadeInUp} className={`${textClasses}`}>
                                    {summary}
                                </motion.p>
                            </LazyMotion>
                            <LazyMotion features={loadFeatures}>
                                <motion.span
                                    variants={variantsFadeInUp}
                                    className={`${textClasses} inline-block uppercase font-headline h5  ${
                                        isDark ? 'emdash-push emdash-push--dark' : 'emdash-push'
                                    } mb-0`}
                                >
                                    Read More
                                </motion.span>
                            </LazyMotion>
                        </>
                    </AnimationFadeLazy>
                </div>
            </div>
            {imageUrl && (
                <AnimationImage isImageLoaded={isImageLoaded}>
                    <CldImage
                        fill
                        sizes="(max-width: 768px) 100vw, 50vw"
                        src={imageUrl}
                        alt={imageAltText}
                        className="object-cover"
                        onLoad={() => setIsImageLoaded(true)}
                    />
                </AnimationImage>
            )}
        </div>
    );
}

export default CardSquare;
